import {PageNav, Scrollbar} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react-lite';
import {useQueryClient} from 'react-query';
import {runGenReportByTitle, runReport} from '../../../../_shared/GWTBridge/invoke/genPage';
import {showConfirmMessage} from '../../../../_shared/Messages/actions/MessageActions';
import {deleteReport} from '../../services/service';
import {monitorTransactionValues, Pages} from '../../consts';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';
import {Report} from '../../stores/ReportStore';
import texts from '../../texts';
import {NoMatchEmptyState, NoReportsEmptyState} from './EmptyState';
import HeaderCol from './HeaderCol';
import RowActions from './RowActions';
import styles from './styles.module.scss';
import {filterAndSort} from './utils';
import {isRTL} from '../../../../Localization';
import {sendAnalyticsMonitor} from '../../../../utils/monitoring';
import {createDataTestId} from '../../../../utils/dataTestId';
import {IReport, IChartConfig} from '../../types';
import {COL_IDS, ITEMS_PER_PAGE} from './consts';
import React, {useMemo, useRef} from 'react';
import {ReportColumns} from './ReportColumns';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Action, Namespace, getStorageKey} from '../../../../services/proto/ServerStorage/utils';
import {getServerStoreData} from '../../../../services/proto/ServerStorage/service';
import {getOuterJoinMapFromServer} from '../../services/reports';

interface Props {
    searchQuery: string;
    onAddReport: () => void;
}

const ReportsList = observer(({searchQuery, onAddReport}: Props) => {
    const queryClient = useQueryClient();
    const {currentScreen} = reportGeneratorsStore;
    const {
        reports,
        session,
        sort,
        currentNavPage,
        setNavPage,
        setSort,
        setCurrentReport,
        setCurrentPage,
        onSaveChartInfo,
        setPrevPosition,
        setChartReady,
        duplicateReport
    } = currentScreen;

    const scrollbarRef = useRef(null);
    const RepChartType = priority.netitems.nrest.RepType.RepType_Chart;
    const OldReportType = priority.netitems.nrest.RepType.RepType_OldReport;
    const RepDataSetType = priority.netitems.nrest.RepType.RepType_DataSet;

    const handleSort = (id: string) => () => {
        const isActive = sort.id === id;
        const isDesc = isActive ? !sort.isDesc : false;
        setSort({id, isDesc});
    };

    const fillChartDataFromStorage = async newReport => {
        const key = getStorageKey({
            nameSpace: Namespace.Analytics,
            action: Action.onChartSave,
            storage: newReport?.reportId.toString()
        });
        const data = await getServerStoreData([key], false, false);
        if (data[key]?.Str) {
            const chart = JSON.parse(data[key]?.Str);
            Object.entries(chart).forEach(([key, value]) => {
                onSaveChartInfo(key as keyof IChartConfig, value);
            });
            setChartReady(true);
        }
    };

    const handleEditReport = async (report: IReport) => {
        const newReport = new Report(report.nodeId, report.id);
        setCurrentReport(newReport);
        const mapInServer = await getOuterJoinMapFromServer(newReport.reportId);
        reportGeneratorsStore.currentScreen.currentReport.setOuterJoinMap(mapInServer);
        const isChart = report.reportType === priority.netitems.nrest.RepType.RepType_Chart;
        if (isChart) {
            await fillChartDataFromStorage(newReport);
            setPrevPosition(Pages.chart);
            setCurrentPage(Pages.chart);
        } else {
            setPrevPosition(Pages.report);
            setCurrentPage(Pages.report);
        }
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsMainEdit, 'success', report.title);
    };

    const handleDeleteReport = async (id: number | Long) => {
        showConfirmMessage({
            title: texts.deleteMsg,
            confirmButtonText: texts.yes,
            cancleButtonText: texts.no,
            defaultButton: 0,
            onConfirm: async () => {
                await deleteReport(id);
                queryClient.invalidateQueries(['reports', session]);
            }
        });
    };

    const handleRunReport = async (
        id: string,
        title: string,
        type: priority.netitems.nrest.RepType
    ) => {
        if (type === RepChartType || type === RepDataSetType || type === OldReportType) {
            const newReport = new Report(id, Number(id));
            setCurrentReport(newReport);
            runReport(id, type);
        } else {
            runGenReportByTitle(title, type);
        }
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsMainRun, 'success', title);
    };

    const handleSetPage = (nextPage: number) => {
        setNavPage(nextPage);
        scrollbarRef?.current?.scrollToTop();
    };

    const filteredList = useMemo(
        () => filterAndSort(searchQuery, sort, reports),
        [searchQuery, reports, sort]
    );

    const startIndex = ITEMS_PER_PAGE * (currentNavPage - 1);
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return (
        <div className={styles.tableContainer}>
            {filteredList.length > 0 ? (
                <div className={styles.table}>
                    <div className={styles.tableHeader}>
                        <HeaderCol
                            title={texts.reportName}
                            isSortActive={sort?.id === COL_IDS.title}
                            onSort={handleSort(COL_IDS.title)}
                            isDesc={sort.isDesc}
                            testId={COL_IDS.title}
                        />
                        <HeaderCol
                            title={texts.baseForm}
                            isSortActive={sort?.id === COL_IDS.baseForm}
                            onSort={handleSort(COL_IDS.baseForm)}
                            isDesc={sort.isDesc}
                            testId={COL_IDS.baseForm}
                        />
                        <HeaderCol
                            title={texts.typeColumn}
                            isSortActive={sort?.id === COL_IDS.reportType}
                            onSort={handleSort(COL_IDS.reportType)}
                            isDesc={sort.isDesc}
                            testId={COL_IDS.reportType}
                        />
                        <HeaderCol
                            title={texts.createdOn}
                            isSortActive={sort?.id === COL_IDS.createdDate}
                            onSort={handleSort(COL_IDS.createdDate)}
                            isDesc={sort.isDesc}
                            testId={COL_IDS.createdDate}
                        />
                        <HeaderCol
                            title={texts.createdBy}
                            isSortActive={sort?.id === COL_IDS.userName}
                            onSort={handleSort(COL_IDS.userName)}
                            isDesc={sort.isDesc}
                            testId={COL_IDS.userName}
                        />
                        <HeaderCol
                            title={texts.updated}
                            isSortActive={sort?.id === COL_IDS.updateTime}
                            onSort={handleSort(COL_IDS.updateTime)}
                            isDesc={sort.isDesc}
                            testId={COL_IDS.updateTime}
                        />
                        <HeaderCol
                            title={texts.shared}
                            isSortActive={sort?.id === COL_IDS.isGeneral}
                            isDesc={sort.isDesc}
                            onSort={handleSort(COL_IDS.isGeneral)}
                            testId={COL_IDS.isGeneral}
                        />
                        <HeaderCol
                            title={texts.active}
                            isSortActive={sort?.id === COL_IDS.isActive}
                            isDesc={sort.isDesc}
                            onSort={handleSort(COL_IDS.isActive)}
                            testId={COL_IDS.isActive}
                        />
                        <div className={styles.tableHeaderItem} />
                    </div>
                    <Scrollbar scrollbarRef={scrollbarRef} rtl={isRTL()}>
                        <div className={styles.tableBody}>
                            {filteredList.slice(startIndex, endIndex).map((report, index) => (
                                <div
                                    key={index}
                                    className={styles.tableRow}
                                    {...createDataTestId('report-list-row')}
                                >
                                    <ReportColumns report={report} searchQuery={searchQuery} />

                                    <RowActions
                                        report={report}
                                        onDelete={handleDeleteReport}
                                        onEdit={handleEditReport}
                                        onDuplicate={duplicateReport}
                                        onRun={handleRunReport}
                                    />
                                </div>
                            ))}
                        </div>
                    </Scrollbar>
                    <PageNav
                        rtl={isRTL()}
                        activePage={currentNavPage}
                        pagesCount={Math.ceil(filteredList.length / ITEMS_PER_PAGE)}
                        onItemClick={handleSetPage}
                        {...createDataTestId('report-list-pagenav', true)}
                        pageNavItemDataId
                    />
                </div>
            ) : reports?.length > 0 ? (
                <NoMatchEmptyState
                    text={`${texts.noMatchEmptyState} \n ${texts.noMatchEmptyState2}`}
                />
            ) : (
                <NoReportsEmptyState
                    onAddReport={onAddReport}
                    text={`${texts.emptyStateTitle} \n ${texts.emptyStateSubTitle}`}
                />
            )}
        </div>
    );
});

export default ReportsList;
